// Stale-chunk recovery (chunk-error-handler.ts)
//
// Problem: after a deploy-with-rsync the old content-hashed assets are
// deleted from /opt/antifraud/web/dist/assets/. A browser session that
// has the old index.html cached (or received it before the deploy) will
// attempt to load old chunk filenames via React Router's lazy() imports.
// Those requests 404, producing a DynamicImportError / TypeError at the
// Suspense boundary.
//
// Solution (two seams):
//
//   safeLazy(factory)
//     A drop-in replacement for React.lazy(). If the dynamic import
//     rejects with an error that looks like a stale-chunk 404, it calls
//     triggerReloadPrompt() and then re-throws so the Suspense/error
//     boundary can handle it.
//
//   installGlobalChunkErrorListeners()
//     Registers window 'error' and 'unhandledrejection' listeners that
//     catch the same pattern for any lazy import that was NOT wrapped in
//     safeLazy (e.g. a sub-route lazily composed inside a feature file).
//     Idempotent — calling it twice is a no-op.
//
//   triggerReloadPrompt()
//     Shows a Sonner toast (the app's existing toast primitive) exactly
//     once. Subsequent calls are no-ops while the prompt is visible.
//     Re-entrant-safe via the `promptShown` flag.
//
// Security: no PII is logged. Error messages that may contain filenames
// are sent only to reportError() which already scrubs before forwarding
// to Sentry.

import { lazy, type ComponentType } from 'react'
import { toast } from 'sonner'
import { reportError } from '@/lib/error-reporter'

// ---------------------------------------------------------------------------
// Chunk-error pattern matching
// ---------------------------------------------------------------------------

// Patterns observed across browsers and bundlers:
//   Chrome:  "Failed to fetch dynamically imported module: https://…"
//   Firefox: "error loading dynamically imported module"
//   Safari:  "Importing a module script failed."
//   Vite:    module-load failures surface as TypeError with above messages
//   webpack: "ChunkLoadError: Loading chunk N failed."
const CHUNK_ERROR_PATTERNS: ReadonlyArray<RegExp> = [
  /failed to fetch dynamically imported module/i,
  /failed to load script/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /loading chunk \d+ failed/i,
  /chunkloaderror/i,
]

export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  const msg = err.message
  const name = err.name
  // Some bundlers (webpack, older Vite plugins) set err.name explicitly.
  if (name === 'ChunkLoadError') return true
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(msg) || re.test(name))
}

// ---------------------------------------------------------------------------
// Reload prompt (idempotent)
// ---------------------------------------------------------------------------

let promptShown = false

export function triggerReloadPrompt(): void {
  if (promptShown) return
  promptShown = true

  toast('The app has been updated. Reload to get the latest version.', {
    id: 'stale-chunk-reload',          // prevents duplicates if Sonner runs
    duration: Infinity,                // non-dismissable via timeout
    action: {
      label: 'Reload now',
      onClick: () => {
        window.location.reload()
      },
    },
    onDismiss: () => {
      // Allow one more prompt if the user dismisses and navigates again.
      promptShown = false
    },
  })
}

// ---------------------------------------------------------------------------
// safeLazy — drop-in for React.lazy()
// ---------------------------------------------------------------------------

type LazyFactory<T extends ComponentType<unknown>> = () => Promise<{ default: T }>

/**
 * Wraps React.lazy() with chunk-load-error recovery.
 *
 * Usage (exact substitute for lazy()):
 *   const MyPage = safeLazy(() => import('@/features/my/Page').then(m => ({ default: m.MyPage })))
 *
 * If the import fails with a stale-chunk pattern the user sees the reload
 * prompt. The error is still re-thrown so the nearest Suspense/error
 * boundary handles the crashed route (empty/spinner is replaced by
 * RouteErrorBoundary, which also shows a Reload button as a second path).
 */
export function safeLazy<T extends ComponentType<unknown>>(factory: LazyFactory<T>) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (isChunkLoadError(err)) {
        reportError(err, { tags: { kind: 'chunk_load_error' } })
        triggerReloadPrompt()
      }
      // Re-throw regardless: the Suspense boundary or RouteErrorBoundary
      // will render a fallback UI. We don't swallow this error.
      throw err
    }),
  )
}

// ---------------------------------------------------------------------------
// Global last-resort listeners
// ---------------------------------------------------------------------------

let listenersInstalled = false

/**
 * Installs window-level 'error' and 'unhandledrejection' listeners that
 * catch stale-chunk failures for any lazy import not wrapped by safeLazy.
 *
 * Idempotent — safe to call multiple times (only installs once).
 * Should be called once at app bootstrap, before React mounts.
 */
export function installGlobalChunkErrorListeners(): void {
  if (listenersInstalled) return
  if (typeof window === 'undefined') return
  listenersInstalled = true

  window.addEventListener('error', (event: ErrorEvent) => {
    if (isChunkLoadError(event.error)) {
      reportError(event.error, { tags: { kind: 'chunk_load_error', source: 'window_error' } })
      triggerReloadPrompt()
      // Do NOT call event.preventDefault() — let existing error boundaries
      // and Sentry's own global handler also receive it.
    }
  })

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      reportError(event.reason, {
        tags: { kind: 'chunk_load_error', source: 'unhandled_rejection' },
      })
      triggerReloadPrompt()
    }
  })
}
